// `/PageConfig` — the document shell, as a form renders it.
//
// A Site carries one of these and a Page carries the override of one, merged
// over its site's by `mergeConfig` in @common/render/page — site first, page on
// top, strings replaced and arrays concatenated, which is gen-2's
// `mergePageConfig` (`archive/recovered/stack-pkg/src/libs/site-render/utils.js`).
//
// Four documents in one file, and they are not interchangeable:
//
//   /PageConfig           what a form renders and what the API validates.
//                         Carries `types`, which bottoms out in the `resolver`
//                         union.
//   /PageConfig/Override  the same shape as a PAGE carries it: one field
//                         differs, and it is `bodyTemplate`. See below.
//   /Data/PageConfig      what the table stores — see ./data.
//   /Data/PageConfig/Override  the override as the table stores it.
//
// Ported from `archive/recovered/stack-pkg/src/schema/page-config`.
import UtilsSchema from "@repoflow/schema/utils";
import * as SchemaLib from "@repoflow/schema";
import TypesSchema from "@common/schema/types";

import {
  title,
  description,
  faviconUrl,
  bodyClasses,
  bodyAttributes,
  bodyTemplate,
  overrideBodyTemplate,
  inlineHead,
  inlineStyle,
  inlineScript,
  includesStyle,
  includesScript,
  metadataList,
  metadataType,
  metadataValue,
  metadataContent,
  attributes,
  attributeName,
  attributeValue,
  types,
  values,
} from "./fields";

// The shell's properties, declared once and shared by both documents below.
//
// One object rather than two literals: the two shapes differ in exactly one
// property and everything else about them has to stay identical, or a field
// added to a site's configuration silently has no page-level override and the
// merge drops it. A spread makes that structural instead of remembered.
const properties = {
  title,
  description,
  faviconUrl,
  bodyClasses,
  bodyAttributes,
  bodyTemplate,
  inlineHead,
  inlineStyle,
  inlineScript,
  includesStyle,
  includesScript,
  metadataList,
  attributes,
  types,
  values,
};

const schema = [
  ...UtilsSchema,
  ...TypesSchema,
  {
    id: "/PageConfig/Metadata",
    type: "object",
    properties: {
      type: metadataType,
      value: metadataValue,
      content: metadataContent,
    },
  },
  {
    id: "/PageConfig/Attribute",
    type: "object",
    properties: {
      name: attributeName,
      value: attributeValue,
    },
  },
  {
    id: "/PageConfig",
    type: "object",
    properties,
  },
  // A page's half of the merge. Every property is the site's, and every one of
  // them means "leave the site's alone" when it holds its empty default — which
  // is what makes an override document possible at all without a per-field
  // "inherit" flag beside each control.
  //
  // `bodyTemplate` is the single exception and the single restatement: the
  // site's is `minLength: 1`, so a page could not express "use my site's
  // template" at all. See overrideBodyTemplate in ./fields.
  {
    id: "/PageConfig/Override",
    type: "object",
    properties: {
      ...properties,
      bodyTemplate: overrideBodyTemplate,
    },
  },
];

// Every property of the shell, at its declared default.
//
// A site created without one, or created before this field existed, would
// otherwise answer `config: null` — and `extract()` returns null unchanged, so
// the form would mount with no object under it and the composer would have no
// body template to slot anything into. Both the create and the resolver seed
// through here, which is also why the shape has a default for every property
// and not only the ones with an obvious one.
export const defaultConfigValues = () =>
  SchemaLib.defaultValues(schema, { $ref: "/PageConfig" });

// The same, for a PAGE. Every property at its empty default, `bodyTemplate`
// included — which is what makes a freshly created page inherit its site's
// shell whole rather than override it with the schema's default template.
//
// gen-2 seeded a page with `{ ...defaults, bodyTemplate: "" }` for exactly that
// reason. Here the empty template IS the declared default of the field the
// override document carries, so the seed is the plain call and there is nothing
// to remember.
export const defaultOverrideValues = () =>
  SchemaLib.defaultValues(schema, { $ref: "/PageConfig/Override" });

export default schema;
